import React from 'react';
import { Box, ButtonBase, Container, Skeleton, Stack, Typography } from '@mui/material';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import ArrowCircleRightRounded from '@mui/icons-material/ArrowCircleRightRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import PhoneInTalkOutlined from '@mui/icons-material/PhoneInTalkOutlined';
import PlaceOutlined from '@mui/icons-material/PlaceOutlined';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import { fetchChatLook, type ChatLook } from '../../../../services/websiteAssistantService';
import ChatWidget from './ChatWidget';
import { useWebFont, withFont } from './webFont';

const NAV = ['Why us?', 'Products', 'Contact', 'Quotes', 'Billing & claims'];

/** "166 Center St, Pembroke, MA 02359" → "Pembroke". */
function townOf(line: string): string | null {
  const parts = line.split(',').map((p) => p.trim());
  return parts.length >= 3 ? parts[parts.length - 2] : null;
}

/** "Pembroke, Plymouth and Osterville, MA", from the offices; empty without them. */
function towns(locations: string[]): string {
  const names = locations.map(townOf).filter((t): t is string => Boolean(t));
  if (!names.length) return '';
  const state = locations[0].split(',').pop()?.trim().split(' ')[0] ?? '';
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0];
  return state ? `${list}, ${state}` : list;
}

/**
 * /w/:key — a stand-in for the agency's own website, with its chat in the corner.
 *
 * The agency's real site is not connected yet, so this page plays it for a
 * demo: the agency's name and colour, a few lines a home and auto agency would
 * have, and the chat. No sign-in: anyone with the link is a visitor. Later the
 * same chat goes onto the real site with one line of code, unchanged.
 */
export default function AgencySitePage() {
  const { key = '' } = useParams();
  const theme = useTheme();
  // The look and the key it was loaded for: a look for another key is never shown, not even
  // for the one render between a change of address and the next load.
  const [loaded, setLoaded] = React.useState<{ key: string; look: ChatLook } | null>(null);
  const look = loaded?.key === key ? loaded.look : null;
  const [missing, setMissing] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [startWith, setStartWith] = React.useState<string | null>(null);
  const font = withFont(useWebFont(look?.font, theme.typography.fontFamily));

  React.useEffect(() => {
    // A late answer for the previous agency must not dress this one in its name and colours.
    let current = true;
    setMissing(null);
    fetchChatLook(key)
      .then((found) => { if (current) setLoaded({ key, look: found }); })
      .catch((err) => { if (current) setMissing(err instanceof Error ? err.message : 'Not found'); });
    return () => { current = false; };
  }, [key]);

  React.useLayoutEffect(() => {
    // Never our own name or icons in the tab, even for a moment: this page speaks for the agency.
    // (The app's own branding leaves pages under /w/ alone: see ownsItsTitle.) What was there
    // before is put back on the way out, so leaving this page restores the app's own.
    const title = document.title;
    const icons = Array.from(document.querySelectorAll<HTMLLinkElement>("link[rel~='icon']"));
    const hrefs = icons.map((icon) => icon.href);
    document.title = look?.agency_name || '';
    icons.forEach((icon) => { icon.href = 'data:,'; });
    return () => {
      document.title = title;
      icons.forEach((icon, i) => { icon.href = hrefs[i]; });
    };
  }, [look]);

  if (missing) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={(t) => ({ minHeight: '100vh', bgcolor: t.tokens.surface.canvas, p: 2 })}>
        <Typography variant="h5" sx={(t) => ({ color: t.tokens.text.primary })}>This page is not available</Typography>
      </Stack>
    );
  }

  if (!look) {
    // Until the agency's look arrives, its outline in plain grey: never our own colours or
    // name, which would flash before the agency's.
    return (
      <Box aria-busy="true" sx={(t) => ({ minHeight: '100vh', bgcolor: t.tokens.surface.canvas })}>
        <Box sx={(t) => ({ bgcolor: t.tokens.surface.raised, boxShadow: t.tokens.shadow.xs })}>
          <Container maxWidth="lg">
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 2.5 }}>
              <Skeleton variant="rounded" width={220} height={48} />
              <Skeleton variant="rounded" width={160} height={44} />
            </Stack>
          </Container>
        </Box>
        <Box sx={(t) => ({ bgcolor: t.tokens.surface.sunken })}>
          <Container maxWidth="md">
            <Stack spacing={2.5} alignItems="center" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 10, md: 14 } }}>
              <Skeleton width="50%" />
              <Skeleton variant="rounded" width="85%" height={96} />
              <Skeleton width="65%" />
              <Skeleton variant="rounded" width={320} height={48} />
            </Stack>
          </Container>
        </Box>
      </Box>
    );
  }

  const brand = look.brand_color || theme.tokens.brand.primary;
  const onBrand = theme.palette.getContrastText(brand);
  const accent = look.accent_color || brand;
  const onAccent = theme.palette.getContrastText(accent);
  const name = look.agency_name || '';
  // "Hannon-Murphy Insurance": the name large, the last word on a bar under it, as agencies sign themselves.
  const words = name.split(' ');
  const [mark, bar] = words.length > 1 ? [words.slice(0, -1).join(' '), words[words.length - 1]] : [name, ''];
  const where = towns(look.locations ?? []);
  const begin = (choice: string) => { setStartWith(choice); setOpen(true); };

  const button = (label: string, onClick: () => void, solid: boolean, icon?: React.ReactNode) => (
    <ButtonBase onClick={onClick} sx={(t) => ({
      px: 2.5, py: 1.25, gap: 1, borderRadius: `${t.ds.radius.xs}px`,
      ...(solid ? { bgcolor: accent, color: onAccent } : { color: onBrand, border: `2px solid ${onBrand}` }),
      '&:hover': { bgcolor: solid ? lighten(accent, 0.1) : alpha(onBrand, 0.1) },
      '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
    })}>
      <Typography variant="subtitle1" sx={{ color: 'inherit', fontWeight: 700 }}>{label}</Typography>
      {icon}
    </ButtonBase>
  );

  const contact = (Icon: React.ElementType, label: string, lines: string[]) => (lines.length ? (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={(t) => ({
        display: 'flex', p: 0.75, borderRadius: '50%', border: `1px solid ${alpha(brand, 0.25)}`,
        bgcolor: t.tokens.surface.raised,
      })}>
        <Icon sx={{ fontSize: 18, color: brand }} />
      </Box>
      <Stack spacing={0}>
        <Typography variant="overline" sx={{ color: brand, fontWeight: 700, lineHeight: 1.4 }}>{label}</Typography>
        {lines.map((line) => (
          <Typography key={line} variant="caption" sx={{ color: alpha(brand, 0.85), textTransform: 'uppercase' }}>
            {line}
          </Typography>
        ))}
      </Stack>
    </Stack>
  ) : null);

  const promise = (Icon: React.ElementType, title: string, words_: string) => (
    <Stack spacing={1.25} alignItems="center" sx={(t) => ({
      p: 3.5, textAlign: 'center', borderRadius: `${t.ds.radius.sm}px`, bgcolor: t.tokens.surface.raised,
      boxShadow: t.tokens.shadow.xl,
    })}>
      <Icon sx={{ fontSize: 36, color: accent }} />
      <Typography variant="h4" sx={{ color: brand }}>{title}</Typography>
      <Typography variant="body1" sx={(t) => ({ color: t.tokens.text.secondary })}>{words_}</Typography>
    </Stack>
  );

  return (
    <Box sx={(t) => ({ minHeight: '100vh', bgcolor: t.tokens.surface.canvas, ...font })}>
      {look && (look.locations.length || look.phone || look.email) ? (
        <Box sx={{ bgcolor: alpha(brand, 0.12), display: { xs: 'none', md: 'block' } }}>
          <Container maxWidth="lg">
            <Stack direction="row" spacing={4} justifyContent="flex-end" sx={{ py: 1 }}>
              {contact(PlaceOutlined, 'Locations:', look.locations)}
              {contact(PhoneInTalkOutlined, 'Phone:', look.phone ? [look.phone] : [])}
              {contact(MailOutlineRounded, 'Email:', look.email ? [look.email] : [])}
            </Stack>
          </Container>
        </Box>
      ) : null}

      <Box sx={(t) => ({ bgcolor: t.tokens.surface.raised, boxShadow: t.tokens.shadow.xs, position: 'relative', zIndex: 1 })}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={3} sx={{ py: 2.5 }}>
            <Stack spacing={0.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
              <Typography variant="h2" sx={{ color: brand, fontStyle: 'italic', fontWeight: 800, textTransform: 'uppercase',
                lineHeight: 1 }}>
                {mark}
              </Typography>
              {bar ? (
                <Box sx={(t) => ({ alignSelf: 'stretch', px: 1.5, bgcolor: accent, borderRadius: `${t.ds.radius.xs}px`,
                  textAlign: 'center' })}>
                  <Typography variant="overline" sx={{ color: onAccent, fontStyle: 'italic', fontWeight: 800,
                    letterSpacing: '0.35em' }}>
                    {bar}
                  </Typography>
                </Box>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={3} alignItems="center">
              <Stack direction="row" spacing={3} sx={{ display: { xs: 'none', md: 'flex' } }}>
                {NAV.map((item) => (
                  <Typography key={item} variant="subtitle2"
                    sx={{ color: lighten(brand, 0.3), fontWeight: 700, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {item}
                  </Typography>
                ))}
              </Stack>
              {button('Start a quote', () => begin('quote'), true, <ArrowCircleRightRounded fontSize="small" />)}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box sx={{ color: onBrand, background: `linear-gradient(160deg, ${lighten(brand, 0.45)} 0%, ${lighten(brand, 0.15)} 45%, ${brand} 100%)` }}>
        <Container maxWidth="md">
          <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center', pt: { xs: 6, md: 10 }, pb: { xs: 10, md: 14 } }}>
            {where ? (
              <Typography variant="overline" sx={{ color: 'inherit', letterSpacing: '0.5em', fontWeight: 700 }}>
                Insurance agency · {where}
              </Typography>
            ) : null}
            <Typography variant="h1" component="h1"
              sx={{ color: 'inherit', typography: { xs: 'displaySm', md: 'displayLg' }, textWrap: 'balance' }}>
              Auto and home insurance, explained plainly{name ? `, with ${name}` : ''}
            </Typography>
            <Typography variant="body1" sx={{ color: 'inherit', opacity: 0.92, maxWidth: 640 }}>
              We compare the carriers that suit you, tell you in plain words what you are paying for, and stay with
              you after the policy starts. Ask us anything in the chat, any time.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              {button('Get an auto quote', () => begin('quote'), true)}
              {button('Ask about my policy', () => begin('question'), false)}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: { xs: -6, md: -8 }, pb: 6, position: 'relative' }}>
        <Box sx={{ display: 'grid', gap: 2.5, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
          {promise(BoltRounded, 'Quick quotes', 'Tell us about your cars and drivers in the chat, or send your '
            + 'declarations page, and we take it from there.')}
          {promise(CompareArrowsRounded, 'A choice of carriers', 'We shop the market for the cover and price '
            + 'that fit you, not the one that suits us.')}
          {promise(SupportAgentRounded, 'Here when you need us', 'A payment, a change, a question about your '
            + 'policy: ask in the chat and we answer from your own records.')}
        </Box>
        <Typography variant="caption" sx={(t) => ({ display: 'block', mt: 5, color: t.tokens.text.tertiary, textAlign: 'center' })}>
          A demonstration page standing in for the agency's own website.
        </Typography>
      </Container>

      {look ? (
        <ChatWidget key={key} chatKey={key} look={look} open={open} onOpenChange={setOpen} startWith={startWith}
          onStarted={() => setStartWith(null)} />
      ) : null}
    </Box>
  );
}
